import React from 'react';
import { AlertCircle } from 'lucide-react';

export interface RadioOption<V extends string = string> {
  value: V;
  label: string;
  description?: string;
  disabled?: boolean;
}

interface RadioGroupProps<V extends string = string> {
  legend: string;
  options: RadioOption<V>[];
  value: V | '';
  onChange: (value: V) => void;
  error?: string;
  /** Nome do input HTML — precisa ser único no form. Default = legend slug. */
  name?: string;
  /** Alinha os radios horizontalmente (default) ou empilhados verticalmente. */
  orientation?: 'horizontal' | 'vertical';
  required?: boolean;
}

/**
 * RadioGroup com o padrão do `UserRegistrationForm` — `fieldset` +
 * `legend`, radios azuis (`text-blue-600 focus:ring-blue-500`), erro
 * embaixo com ícone `AlertCircle`. Substitui os pares `<input
 * type="radio">` inline espalhados pelas features.
 */
export function RadioGroup<V extends string = string>({
  legend,
  options,
  value,
  onChange,
  error,
  name,
  orientation = 'horizontal',
  required,
}: RadioGroupProps<V>) {
  const generatedId = React.useId();
  const groupName = name ?? `radio-${generatedId}`;

  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-semibold text-gray-700">
        {legend}
        {required && ' *'}
      </legend>
      <div className={orientation === 'horizontal' ? 'flex flex-wrap gap-6' : 'flex flex-col gap-2'}>
        {options.map((option) => (
          <label
            key={option.value}
            className={`flex items-start gap-2 text-sm text-gray-700 ${
              option.disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
            }`}
          >
            <input
              type="radio"
              name={groupName}
              value={option.value}
              checked={value === option.value}
              disabled={option.disabled}
              onChange={() => onChange(option.value)}
              className="mt-0.5 h-4 w-4 text-blue-600 focus:ring-blue-500"
            />
            <span>
              <span>{option.label}</span>
              {option.description && (
                <span className="mt-0.5 block text-xs text-gray-500">
                  {option.description}
                </span>
              )}
            </span>
          </label>
        ))}
      </div>
      {error && (
        <p className="flex items-center gap-1 text-sm text-red-600">
          <AlertCircle className="w-4 h-4" />
          {error}
        </p>
      )}
    </fieldset>
  );
}
